/**
 * Sentry initialization and utilities.
 * Configure via Vite env: VITE_SENTRY_DSN, VITE_SENTRY_ENABLED, VITE_SENTRY_ENVIRONMENT,
 * VITE_SENTRY_TRACES_SAMPLE_RATE, VITE_SENTRY_DEBUG (optional, set to true to enable console logging).
 */
import * as Sentry from '@sentry/react'

const env = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env : {}
const dsn = env.VITE_SENTRY_DSN
const enabled = dsn && (env.VITE_SENTRY_ENABLED || '').toLowerCase() === 'true'
const environment = env.VITE_SENTRY_ENVIRONMENT || env.MODE || 'development'
const tracesSampleRate = parseFloat(env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0', 10) || 0.1
const debug = (env.VITE_SENTRY_DEBUG || '').toLowerCase() === 'true'

export const isSentryEnabled = () => !!enabled

/**
 * Initialize Sentry. Call once at app bootstrap (e.g. in main.jsx).
 * Set VITE_SENTRY_DEBUG=true to enable Sentry debug logs in the console.
 */
export function initSentry() {
  if (!enabled) return

  Sentry.init({
    dsn,
    environment,
    tracesSampleRate,
    sendDefaultPii: true,
    debug,
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/.*\.tubatoolbox\.com/,
          /^https:\/\/.*\.tuba\.cl/,
        ],
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      const error = hint?.originalException
      if (error?.message) {
        if (error.message.includes('chrome-extension://')) return null
        if (error.message.includes('adblock')) return null
      }
      return event
    },
    ignoreErrors: [
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'NetworkError',
      'Failed to fetch',
      'Network request failed',
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i],
  })
}

export { Sentry }
